import React, { memo, useCallback, useMemo } from 'react'
import Box from 'transfix-ui/core/Box'
import AutocompleteLocation, { IAutocompleteLocationProps } from '../AutocompleteLocation'
import SelectRadius from '../SelectRadius'
import { ISelectRadiusProps } from '../SelectRadius/SelectRadius'

export interface IAutocompleteLocationRadiusValue {
  location?: IAutocompleteLocationProps['value']
  radius?: ISelectRadiusProps['value']
}

export interface IAutocompleteLocationRadiusProps {
  value: IAutocompleteLocationRadiusValue | null
  /**
   * @default false
   */
  enableAnyLocation?: boolean
  /**
   * @default 'Any Location'
   */
  anyLocationOptionLabel?: string
  dataTest?: string
  className?: string
  classNameRadiusWrapper?: string
  AutocompleteLocationProps: Omit<IAutocompleteLocationProps, 'value' | 'onChange'>
  SelectRadiusProps: Omit<ISelectRadiusProps, 'value' | 'ref' | 'onChange'>
  onChange: <
    K extends keyof IAutocompleteLocationRadiusValue,
    V extends IAutocompleteLocationRadiusValue[K]
  >(
    key: K,
    value: V | null
  ) => void
}

const AutocompleteLocationRadius = memo<IAutocompleteLocationRadiusProps>(
  ({
    AutocompleteLocationProps,
    SelectRadiusProps,
    anyLocationOptionLabel = 'Any Location',
    className,
    classNameRadiusWrapper,
    dataTest,
    enableAnyLocation = false,
    onChange,
    value,
  }) => {
    const { placeholder: placeholderProp } = AutocompleteLocationProps || {}
    const { location, radius } = value || {}

    const placeholder = enableAnyLocation ? anyLocationOptionLabel : placeholderProp

    const showRadiusSelect = useMemo(() => {
      return !enableAnyLocation || (enableAnyLocation && location)
    }, [enableAnyLocation, location])

    const locationChangeHandler = useCallback(
      newVal => {
        onChange('location', newVal)
      },
      [onChange]
    )

    const radiusChangeHandler: ISelectRadiusProps['onChange'] = event => {
      const { target } = event || {}
      const { value } = target || {}
      onChange('radius', value)
    }

    return (
      <Box data-test={dataTest} display='flex' className={className}>
        <Box flexGrow={1} flexShrink={0}>
          <AutocompleteLocation
            value={location || null}
            onChange={locationChangeHandler}
            {...AutocompleteLocationProps}
            placeholder={placeholder}
          />
        </Box>
        {showRadiusSelect && (
          <Box className={classNameRadiusWrapper}>
            <SelectRadius value={radius} onChange={radiusChangeHandler} {...SelectRadiusProps} />
          </Box>
        )}
      </Box>
    )
  }
)

export default AutocompleteLocationRadius
